import React, { ChangeEvent, useCallback, useEffect, useState } from "react";
import { getMapMarkers } from "../../api/mapMarkers/mapMarkersApi";
import { useNavigate, useParams } from "react-router-dom";
import {
  MapMarker,
  Organization,
  Video,
  VideoCataloguesDto,
} from "../common/ObjectTypes";
import {
  createVideoCatalogue,
  getVideoCatalogueDetails,
  updateVideoCatalogue,
  updateCatalogueMapMarkers,
  updateCatalogueVideos,
} from "../../api/videoCatalogues.tsx/videoCataloguesApi";
import { toast } from "react-toastify";
import { MoonLoader } from "react-spinners";
import AddEditCatalogue from "./AddEditCatalogue";
import { getMapMarkerVideos } from "../../api/videos/videosApi";
import { searchVideos } from "../../api/videos/videosApi";
import { getCurrentUser } from "../../helpers/authHeader";
import { getOrganizations } from "../../api/organizations/organizationsApi";
import { getWildXROrgId } from "../../helpers/wildXROrgIdHelper";

type ManageAddEditCatalogueProps = {
  existingCatalogue: boolean;
  selectMapMarkersPage?: boolean;
  selectVideosPage?: boolean;
  readOnly?: boolean;
};

const ManageAddEditCatalogue = ({
  existingCatalogue,
  selectMapMarkersPage,
  selectVideosPage,
  readOnly,
}: ManageAddEditCatalogueProps) => {
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const wildXRId = getWildXROrgId();
  const videoCatalogueId = useParams<{ id: string }>().id;
  const [pageLoaded, setPageLoaded] = useState(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [errors, setErrors] = useState({});
  const [changesMade, setChangesMade] = useState(false);
  const [catalogue, setCatalogue] = useState<VideoCataloguesDto>({
    videoCatalogueId: "",
    name: "",
    description: "",
    siteId: "",
    organizationId: "",
    lastEditedBy: "",
    lastEditedDate: "",
    videos: [],
    mapMarkers: [],
  });
  const [mapMarkers, setMapMarkers] = useState<MapMarker[]>([]);

  const [videos, setVideos] = useState<Video[]>([]);
  const { v4: uuidv4 } = require("uuid");
  const [saving, setSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [areAllMapMarkersSelected, setAreAllMapMarkersSelected] =
    useState(false);
  const [areAllVideosSelected, setAreAllVideosSelected] = useState(false);

  useEffect(() => {
    const loadVideoCatalogue = async () => {
      try {
        if (videoCatalogueId) {
          const temp = await getVideoCatalogueDetails(videoCatalogueId);
          setCatalogue(temp);
        }
      } catch (err) {
        console.log(err);
      } finally {
        setPageLoaded(true); // Ensure this is only called once
      }
    };

    const loadOrganizations = async () => {
      try {
        let temp = await getOrganizations();
        setOrganizations(temp);
      } catch (err) {
        console.log(err);
      }
    };

    loadVideoCatalogue();
    loadOrganizations();
  }, [videoCatalogueId]);

  const handleChangeThumbnail = (e: any) => {
    const file = e.target.files[0];
    setCatalogue((prevCatalogue) => ({
      ...prevCatalogue,
      thumbnailUrl: file.name,
    }));
  };

  const fetchVideosByMapMarkerIds = useCallback(
    async (mapMarkerIds: string[]) => {
      try {
        const resp = await getMapMarkerVideos(mapMarkerIds, 1, 1000);
        if (!resp) {
          throw new Error("Failed to fetch videos");
        }
        return resp.results;
      } catch (error) {
        console.error("Error fetching videos:", error);
        return [];
      }
    },
    []
  );
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    if (name === "overview") {
      setCatalogue((prevCatalogue) => {
        const updatedCatalogue = {
          ...prevCatalogue,
          description: newValue.toString(),
        };

        return updatedCatalogue;
      });
    }
    setCatalogue((prevCatalogue) => {
      const updatedCatalogue = {
        ...prevCatalogue,
        [name]: newValue,
      };
      return updatedCatalogue;
    });
    setChangesMade(true);
  };

  const handleChangeSingleSelect = (name: string, e: any) => {
    setCatalogue((prevCatalogue: any) => {
      const newValue = e === null ? null : e.value;
      const updatedCatalogue = {
        ...prevCatalogue,
        [name]: newValue,
      };
      return updatedCatalogue;
    });
    setChangesMade(true);
  };

  const handleSelectVideoForCatalogue = (
    selectedVideo: Video,
    isChecked: boolean
  ) => {
    setCatalogue((prevCatalogue: any) => {
      const currentVideos = prevCatalogue.videos || [];
      const videoObject = {
        videoId: selectedVideo.videoId,
        thumbnailUrl: selectedVideo.thumbnailUrl,
        name: selectedVideo.name,
        dateCreated: selectedVideo.dateCreated,
        lastEditedBy: selectedVideo.lastEditedBy,
        lastEditedDate: selectedVideo.lastEditedDate,
        overview: selectedVideo.overview,
        countryObtainedId: selectedVideo.countryObtainedId,
        species: selectedVideo.species || [],
        tags: selectedVideo.tags || [],
        mapMarkers: selectedVideo.mapMarkers || [],
        rowVersion: selectedVideo.rowVersion,
        streamingUrl: selectedVideo.streamingUrl,
        downloadUrl: selectedVideo.downloadUrl,
        totalViews: selectedVideo.totalViews,
        totalLikes: selectedVideo.totalLikes,
        totalDislikes: selectedVideo.totalDislikes,
        rating: selectedVideo.rating,
        youTubeUrl: selectedVideo.youTubeUrl,
        startTime: selectedVideo.startTime,
        endTime: selectedVideo.endTime,
        showInAllVideos: selectedVideo.showInAllVideos,
        compilation: selectedVideo.compilation,
        gameDrive: selectedVideo.gameDrive,
        graphicSex: selectedVideo.graphicSex,
        graphicDeath: selectedVideo.graphicDeath,
        videoFormat: selectedVideo.videoFormat,
        videoStatusId: selectedVideo.videoStatusId,
      };
      const newVideos = isChecked
        ? currentVideos.some((v: any) => v.videoId === selectedVideo.videoId)
          ? currentVideos
          : [...currentVideos, videoObject]
        : currentVideos.filter((v: any) => v.videoId !== selectedVideo.videoId);
      const updatedCatalogue = {
        ...prevCatalogue,
        videos: newVideos,
      };
      return updatedCatalogue;
    });
    setChangesMade(true);
  };

  const handleSelectAllMapMarkers = async () => {
    setIsLoading(true); // Start loading
    try {
      // Get current custom map markers (those with organizationId !== wildXRId)
      const customMapMarkers = (catalogue.mapMarkers || []).filter(
        (mapMarker: MapMarker) => mapMarker.organizationId !== wildXRId
      );

      if (areAllMapMarkersSelected) {
        // Deselect all non-custom mapMarkers, but keep custom ones
        const customMapMarkerIds = customMapMarkers.map(
          (marker: MapMarker) => marker.mapMarkerId
        );

        // Fetch videos associated with the custom map markers
        const customMarkerVideos =
          customMapMarkerIds.length > 0
            ? await fetchVideosByMapMarkerIds(customMapMarkerIds)
            : [];

        setCatalogue((prevCatalogue: any) => ({
          ...prevCatalogue,
          mapMarkers: customMapMarkers,
          videos: customMarkerVideos || [],
        }));
        setAreAllMapMarkersSelected(false);
      } else {
        // Fetch all mapMarkers and their associated videos
        const allMapMarkers = await getMapMarkers();
        // Filter to include only wildXR map markers
        const wildXRMapMarkers = allMapMarkers.filter(
          (mapMarker: MapMarker) => mapMarker.organizationId === wildXRId
        );

        // Combine wildXR map markers with existing custom map markers
        const combinedMapMarkers = [...wildXRMapMarkers, ...customMapMarkers];

        // Get all map marker IDs for video fetching
        const mapMarkerIds = combinedMapMarkers.map(
          (mapMarker: MapMarker) => mapMarker.mapMarkerId
        );
        const videos = await fetchVideosByMapMarkerIds(mapMarkerIds);

        setCatalogue((prevCatalogue: any) => ({
          ...prevCatalogue,
          mapMarkers: combinedMapMarkers,
          videos: videos || [],
        }));
        setAreAllMapMarkersSelected(true);
      }
    } catch (error) {
      console.error("Error selecting/deselecting all mapMarkers:", error);
    }
    setChangesMade(true);
    setIsLoading(false); // End loading
  };

  const handleSelectAllVideos = async () => {
    setIsLoading(true); // Start loading
    try {
      if (areAllVideosSelected) {
        // Deselect all videos
        // First update the areAllVideosSelected state to ensure UI updates properly
        setAreAllVideosSelected(false);

        // Then update the catalogue
        setCatalogue((prevCatalogue: any) => ({
          ...prevCatalogue,
          videos: [],
        }));
      } else {
        // Check if map markers are selected
        if (catalogue.mapMarkers && catalogue.mapMarkers.length > 0) {
          // Get the map marker IDs
          const mapMarkerIds = catalogue.mapMarkers.map(
            (mapMarker: MapMarker) => mapMarker.mapMarkerId
          );

          // Fetch videos associated with these map markers
          const videosForMapMarkers = await fetchVideosByMapMarkerIds(
            mapMarkerIds
          );

          // First update the areAllVideosSelected state to ensure UI updates properly
          setAreAllVideosSelected(true);

          // Then update the catalogue
          setCatalogue((prevCatalogue: any) => ({
            ...prevCatalogue,
            videos: videosForMapMarkers || [],
          }));
        } else {
          // If no map markers selected, fetch all videos
          const temp = await searchVideos(
            { pageNumber: 1, pageSize: 1000 },
            []
          );
          const videos = temp.results;

          // First update the areAllVideosSelected state to ensure UI updates properly
          setAreAllVideosSelected(true);

          // Then update the catalogue
          setCatalogue((prevCatalogue: any) => ({
            ...prevCatalogue,
            videos: videos || [],
          }));
        }
      }
    } catch (error) {
      console.error("Error selecting/deselecting all videos:", error);
      toast.error("Error selecting/deselecting all videos", {
        position: toast.POSITION.TOP_RIGHT,
        hideProgressBar: true,
        autoClose: 2500,
      });
    }
    setChangesMade(true);
    setIsLoading(false); // End loading
  };

  const handleChangeMapMarker = (
    selectedMapMarker: MapMarker,
    isChecked: boolean
  ) => {
    setCatalogue((prevCatalogue: any) => {
      const currentMapMarkers = prevCatalogue?.mapMarkers || [];

      const mapMarkerObject = {
        mapMarkerId: selectedMapMarker.mapMarkerId,
        name: selectedMapMarker.name,
        rowVersion: selectedMapMarker.rowVersion,
        videos: selectedMapMarker.videos || [], // Not used for filtering in this case
        latitude: selectedMapMarker.latitude,
        longitude: selectedMapMarker.longitude,
        iconID: selectedMapMarker.iconID,
        description: selectedMapMarker.description || "",
        organizationId: selectedMapMarker.organizationId || "",
      };

      const newMapMarkers = isChecked
        ? currentMapMarkers.some(
            (p: any) => p.mapMarkerId === selectedMapMarker.mapMarkerId
          )
          ? currentMapMarkers
          : [...currentMapMarkers, mapMarkerObject]
        : currentMapMarkers.filter(
            (p: any) => p.mapMarkerId !== selectedMapMarker.mapMarkerId
          );

      return {
        ...prevCatalogue,
        mapMarkers: newMapMarkers,
      };
    });

    fetchVideosByMapMarkerIds([selectedMapMarker.mapMarkerId]).then(
      (fetchedVideos) => {
        setCatalogue((prevCatalogue: any) => {
          const currentMapMarkers = prevCatalogue.mapMarkers || [];
          let updatedVideos = prevCatalogue?.videos || [];

          if (isChecked) {
            // Add newly fetched videos to the list, avoiding duplicates
            const uniqueNewVideos = (fetchedVideos || []).filter(
              (newVideo: Video) =>
                !updatedVideos.some(
                  (existingVideo: Video) =>
                    existingVideo.videoId === newVideo.videoId
                )
            );
            updatedVideos = [...updatedVideos, ...uniqueNewVideos];
          } else {
            // Remove videos no longer associated with any selected map markers
            const remainingMapMarkerIds = currentMapMarkers.map(
              (marker: any) => marker.mapMarkerId
            );

            // Fetch videos for all remaining selected map markers
            const remainingVideos = remainingMapMarkerIds.length
              ? fetchVideosByMapMarkerIds(remainingMapMarkerIds)
              : Promise.resolve([]);

            // Filter the updated videos based on their presence in remaining map marker videos
            remainingVideos.then((videosToKeep) => {
              updatedVideos = updatedVideos.filter((video: Video) =>
                videosToKeep.some(
                  (remainingVideo: Video) =>
                    remainingVideo.videoId === video.videoId
                )
              );

              // Update the catalogue with the filtered videos
              setCatalogue({
                ...prevCatalogue,
                videos: updatedVideos,
              });
            });
          }

          return {
            ...prevCatalogue,
            videos: updatedVideos, // Tentatively update here for immediate UI
          };
        });
      }
    );

    setChangesMade(true); // Trigger a rerender
  };

  const getMapMarkerIdsFromCatalogue = useCallback(() => {
    try {
      const mapMarkersArray = catalogue.mapMarkers || [];
      const mapMarkerIds = mapMarkersArray.map(
        (mapMarker: any) => mapMarker.mapMarkerId
      );
      return mapMarkerIds;
    } catch (error) {
      console.error("Failed to parse video from localStorage", error);
      return [];
    }
  }, [catalogue.mapMarkers]);

  const getVideoIdsFromCatalogue = useCallback(() => {
    try {
      const videosArray = catalogue.videos || [];
      const videoIds = videosArray.map((video: any) => video.videoId);
      return videoIds;
    } catch (error) {
      console.error("Failed to parse video from localStorage", error);
      return [];
    }
  }, [catalogue.videos]);

  // Check if all WPS map markers are included in the catalogue
  const checkIfAllWpsMapMarkersSelected = useCallback(async () => {
    try {
      // Get all map markers from the API
      const allMapMarkers = await getMapMarkers();

      // Filter to get only WPS map markers
      const wpsMapMarkers = allMapMarkers.filter(
        (mapMarker: MapMarker) => mapMarker.organizationId === wildXRId
      );

      // Get the map marker IDs currently in the catalogue
      const catalogueMapMarkerIds = getMapMarkerIdsFromCatalogue();

      // Check if all WPS map markers are in the catalogue
      const allWpsMarkersIncluded = wpsMapMarkers.every((marker: MapMarker) =>
        catalogueMapMarkerIds.includes(marker.mapMarkerId)
      );

      // Update the state
      setAreAllMapMarkersSelected(
        allWpsMarkersIncluded && wpsMapMarkers.length > 0
      );
    } catch (error) {
      console.error("Error checking if all map markers are selected:", error);
    }
  }, [wildXRId, getMapMarkerIdsFromCatalogue]);

  // Add an effect to check if all WPS map markers are selected when catalogue.mapMarkers changes
  useEffect(() => {
    if (pageLoaded) {
      checkIfAllWpsMapMarkersSelected();
    }
  }, [catalogue.mapMarkers, pageLoaded, checkIfAllWpsMapMarkersSelected]);

  // Check if all videos for the selected map markers are included in the catalogue
  useEffect(() => {
    const checkIfAllVideosAreSelected = async () => {
      if (pageLoaded) {
        try {
          // If we have map markers selected, check if all associated videos are selected
          if (catalogue.mapMarkers && catalogue.mapMarkers.length > 0) {
            // Get the map marker IDs
            const mapMarkerIds = catalogue.mapMarkers.map(
              (mapMarker: MapMarker) => mapMarker.mapMarkerId
            );

            // Fetch videos associated with these map markers
            const availableVideosForMapMarkers =
              await fetchVideosByMapMarkerIds(mapMarkerIds);

            // Get the videoIds currently in the catalogue
            const catalogueVideoIds = getVideoIdsFromCatalogue();

            if (availableVideosForMapMarkers.length === 0) {
              // No videos available for these map markers
              setAreAllVideosSelected(false);
              return;
            }

            // Check if all videos associated with the selected map markers are included in the catalogue
            const allMapMarkerVideosIncluded =
              availableVideosForMapMarkers.every((video: Video) =>
                catalogueVideoIds.includes(video.videoId)
              );

            // Update the state - all videos are selected if every available video for selected map markers
            // is in the catalogue, and the counts match (to ensure none are missing)
            setAreAllVideosSelected(
              allMapMarkerVideosIncluded &&
                availableVideosForMapMarkers.length > 0 &&
                catalogueVideoIds.length >= availableVideosForMapMarkers.length
            );
          } else {
            // If no map markers selected, check against all videos
            const temp = await searchVideos(
              { pageNumber: 1, pageSize: 1000 },
              []
            );
            const allAvailableVideos = temp.results;

            // Get the videoIds currently in the catalogue
            const catalogueVideoIds = getVideoIdsFromCatalogue();

            // Check if all available videos are in the catalogue
            const allVideosIncluded = allAvailableVideos.every((video: Video) =>
              catalogueVideoIds.includes(video.videoId)
            );

            // Update the state
            setAreAllVideosSelected(
              allVideosIncluded &&
                allAvailableVideos.length > 0 &&
                catalogueVideoIds.length === allAvailableVideos.length
            );
          }
        } catch (error) {
          console.error("Error checking if all videos are selected:", error);
        }
      } else {
        // Reset the state when page is not loaded
        setAreAllVideosSelected(false);
      }
    };

    checkIfAllVideosAreSelected();
  }, [
    catalogue.videos,
    catalogue.mapMarkers,
    pageLoaded,
    getVideoIdsFromCatalogue,
    fetchVideosByMapMarkerIds,
  ]);

  const formIsValid = (): boolean => {
    const newErrors: any = {};

    if (!catalogue.name) {
      newErrors.name = "Video name is required";
    }
    if (!catalogue.description) {
      newErrors.overview = "Description is required";
    }
    if (!catalogue.mapMarkers) {
      newErrors.mapMarkers = "At least one map marker required.";
    }
    if (!catalogue.videos) {
      newErrors.videos = "At least one video required.";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (e: any) => {
    e.preventDefault();
    const mapMarkerIds = getMapMarkerIdsFromCatalogue();
    const videoIds = getVideoIdsFromCatalogue();

    const updatedCatalogue = {
      name: catalogue.name,
      description: catalogue.description,
      videoCatalogueId: catalogue.videoCatalogueId || uuidv4(),
      organizationId: catalogue.organizationId
        ? catalogue.organizationId
        : currentUser.organizations.length === 1
        ? currentUser.organizations[0].organizationId
        : null,
      mapMarkers: [],
      videos: [],
      lastEditedDate: catalogue.lastEditedDate
        ? catalogue.lastEditedDate
        : new Date(),
    };

    try {
      setSaving(true);
      let canSave = formIsValid();
      if (!canSave) return;

      if (!existingCatalogue) await createVideoCatalogue(updatedCatalogue);
      else await updateVideoCatalogue(updatedCatalogue);

      await updateCatalogueMapMarkers(
        updatedCatalogue.videoCatalogueId,
        mapMarkerIds
      );
      await updateCatalogueVideos(updatedCatalogue.videoCatalogueId, videoIds);
      toast.success(
        catalogue.name + (existingCatalogue ? " updated." : " added."),
        {
          position: toast.POSITION.TOP_RIGHT,
          hideProgressBar: true,
          autoClose: 2500,
        }
      );

      navigate("/videoCatalogues");
      setPageLoaded(false);
    } catch (error) {
      console.log(error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/videoCatalogues");
  };
  return !pageLoaded ? (
    <div className="flex justify-center items-center">
      <MoonLoader color="#9EB45B" />
    </div>
  ) : (
    <div className="h-full">
      <AddEditCatalogue
        catalogue={catalogue}
        existingCatalogue={existingCatalogue}
        mapMarkers={mapMarkers}
        selectedVideos={videos}
        organizations={organizations}
        errors={errors}
        onChange={handleChange}
        onChangeThumbnail={handleChangeThumbnail}
        onCancel={handleCancel}
        onSave={handleSave}
        selectMapMarkersPage={selectMapMarkersPage ? true : false}
        selectVideosPage={selectVideosPage ? true : false}
        onChangeMapMarker={handleChangeMapMarker}
        onSelectAllMapMarkers={handleSelectAllMapMarkers}
        onSelectVideoForCatalogue={handleSelectVideoForCatalogue}
        onSelectAllVideos={handleSelectAllVideos}
        areAllMapMarkersSelected={areAllMapMarkersSelected}
        areAllVideosSelected={areAllVideosSelected}
        changesMade={changesMade}
        saving={saving}
        isLoading={isLoading}
        onChangeSingleSelect={handleChangeSingleSelect}
      />
    </div>
  );
};

export default ManageAddEditCatalogue;
